export default class EmailValidator {
  private static readonly validUserChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789._-";
  private static readonly validUserStartEnd =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  private static readonly validDomainChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-";
  private static readonly validDomainStartEnd =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

  private static readonly blockedDomains = [
    "example.com",
    "example.net",
    "example.org",
    "test.com",
    "test",
    "invalid",
    "localhost",
    "local",
    "fake.com",
    "noemail.com",
    "mailnator.com",
    "tempmail.com",
    "10minutemail.com",
    "guerrillamail.com",
    "email.com",
    "donotreply.com",
    "noreply.com",
    "yourdomain.com",
    "mydomain.com",
    "example.local",
    "undefined",
    "undefine",
    "undefin",
    "undefined.com",
    "undefine.com",
    "undefin.com",
  ];

  private static readonly blockedUserTokens = [
    "test",
    "fake",
    "demo",
    "example",
    "undefined",
  ];

  private static readonly blockedUserPrefixes = [
    "noreply",
    "no-reply",
    "donotreply",
    "do-not-reply",
  ];

  public static validate(email: string): string | null {
    if (!email || email.trim() === "") {
      return "El correo está vacío.";
    }

    const atCount = (email.match(/@/g) || []).length;
    if (atCount !== 1) {
      return "El correo debe tener una sola '@'.";
    }

    const [user, domain] = email.split("@");
    if (!user || !domain) {
      return "El correo debe tener parte de usuario y dominio válidos.";
    }

    const loweredUser = user.toLowerCase();
    const loweredDomain = domain.toLowerCase();

    if (this.blockedDomains.includes(loweredDomain)) {
      return "El dominio de correo no es válido.";
    }

    if (this.isBlockedUser(loweredUser, loweredDomain)) {
      return "La cuenta de correo contiene patrones no válidos.";
    }

    const userError = this.validatePart(
      user,
      this.validUserChars,
      this.validUserStartEnd,
      this.validUserStartEnd,
      "cuenta"
    );
    if (userError) return userError;

    const domainError = this.validatePart(
      domain,
      this.validDomainChars,
      this.validDomainStartEnd,
      this.validDomainStartEnd,
      "dominio",
      true
    );
    if (domainError) return domainError;

    const labelError = this.validateDomainLabels(domain);
    if (labelError) return labelError;

    return null;
  }

  private static isBlockedUser(
    loweredUser: string,
    loweredDomain: string
  ): boolean {
    const userTokens = loweredUser.split(/[._-]+/).filter(Boolean);

    const blockedTokenHit = userTokens.some((t) =>
      this.blockedUserTokens.includes(t)
    );

    const blockedPrefixHit = this.blockedUserPrefixes.some((p) =>
      loweredUser.startsWith(p)
    );

    const concatenated = `${loweredUser}${loweredDomain}`;
    const specialHit = concatenated.includes("notiene");

    return blockedTokenHit || blockedPrefixHit || specialHit;
  }

  private static validatePart(
    part: string,
    allowedChars: string,
    startAllowed: string,
    endAllowed: string,
    label: "cuenta" | "dominio",
    mustContainDot = false
  ): string | null {
    const firstChar = part[0]?.toUpperCase();
    const lastChar = part[part.length - 1]?.toUpperCase();

    if (!startAllowed.includes(firstChar)) {
      return `La ${label} no puede comenzar con "${firstChar}".`;
    }

    if (!endAllowed.includes(lastChar)) {
      return `La ${label} no puede terminar con "${lastChar}".`;
    }

    if (mustContainDot && !part.includes(".")) {
      return `El ${label} debe contener un punto ".".`;
    }

    for (let i = 0; i < part.length; i++) {
      const char = part[i];
      const upperChar = char.toUpperCase();

      if (!allowedChars.includes(upperChar)) {
        if (char === " ")
          return `La ${label} no puede contener espacios en blanco.`;
        return `La ${label} no puede contener el caracter "${char}".`;
      }

      if (
        (char === "." && part[i - 1] === ".") ||
        (label === "dominio" && char === "-" && part[i - 1] === "-")
      ) {
        return `La ${label} no puede contener caracteres repetidos "${char}".`;
      }
    }

    return null;
  }

  private static validateDomainLabels(domain: string): string | null {
    const labels = domain.split(".");
    if (labels.some((l) => l.length === 0)) {
      return "El dominio no puede tener etiquetas vacías (\"..\" o \".\" al inicio/fin).";
    }

    for (const l of labels) {
      if (l.startsWith("-") || l.endsWith("-")) {
        return "Cada etiqueta del dominio no puede iniciar ni terminar con \"-\".";
      }
    }

    const tld = labels[labels.length - 1];
    if (tld.length < 2) {
      return "El dominio debe tener un TLD válido (mínimo 2 caracteres).";
    }

    return null;
  }
}
